import {
  Box,
  HStack,
  IconButton,
  Input,
  NativeSelect,
  RadioGroup,
  SimpleGrid,
  Switch,
  Text,
  Textarea,
  VStack
} from "@chakra-ui/react"
import useCopyToClipboard from "@pathwright/utils/src/clipboard/useCopyToClipboard"
import { Form, Formik, useFormikContext } from "formik"
import { useEffect, useMemo, useRef, useState } from "react"
import Pathicon from "../pathicon/Pathicon"

export type EmbedFieldType = "text" | "number" | "select" | "switch" | "radio"

export type EmbedFieldOption = {
  label: string
  value: string
}

export type EmbedFieldConfig<TValues extends Record<string, unknown>> = {
  name: keyof TValues & string
  label: string
  type: EmbedFieldType
  colSpan?: 1 | 2
  placeholder?: string
  helpText?: string
  options?: EmbedFieldOption[]
}

/** Defaults match package embed iframes; override for other embed features that post resize heights. */
export const EMBED_PREVIEW_RESIZE_POST_MESSAGE_SOURCE_DEFAULT =
  "pathwright:package-embed"
export const EMBED_PREVIEW_RESIZE_POST_MESSAGE_TYPE_DEFAULT = "resize"

type EmbedCodeGeneratorFormProps<TValues extends Record<string, unknown>> = {
  fields: EmbedFieldConfig<TValues>[]
  buildCode: (values: TValues) => string
  copySuccessTitle: string
  postMessageResizeSource: string
  postMessageResizeType: string
}

function EmbedCodeGeneratorForm<TValues extends Record<string, unknown>>({
  fields,
  buildCode,
  copySuccessTitle,
  postMessageResizeSource,
  postMessageResizeType
}: EmbedCodeGeneratorFormProps<TValues>) {
  const formik = useFormikContext<TValues>()
  const copyToClipboard = useCopyToClipboard()

  const generatedCode = useMemo(
    () => buildCode(formik.values),
    [buildCode, formik.values]
  )
  const [code, setCode] = useState(generatedCode)
  const [previewHeight, setPreviewHeight] = useState(250)
  const previewIframeRef = useRef<HTMLIFrameElement>(null)
  const previewSrc = useMemo(() => {
    const srcMatch = code.match(/<iframe[^>]*\ssrc="([^"]+)"/i)
    return srcMatch?.[1] || ""
  }, [code])

  useEffect(() => {
    setCode(generatedCode)
  }, [generatedCode])

  useEffect(() => {
    setPreviewHeight(250)
  }, [previewSrc])

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      const data = event.data as
        | { source?: string; type?: string; height?: number }
        | undefined
      if (!data || data.source !== postMessageResizeSource) return
      if (data.type !== postMessageResizeType) return
      if (event.source !== previewIframeRef.current?.contentWindow) return
      if (typeof data.height === "number" && data.height > 0) {
        setPreviewHeight(data.height)
      }
    }

    window.addEventListener("message", handleMessage)
    return () => window.removeEventListener("message", handleMessage)
  }, [postMessageResizeSource, postMessageResizeType])

  return (
    <VStack w="100%" alignItems="stretch" gap={2}>
      <SimpleGrid columns={2} gap={4}>
        {fields.map((field) => {
          const value = formik.values[field.name]

          return (
            <VStack
              key={field.name}
              alignItems="stretch"
              gap={1}
              gridColumn={field.colSpan === 2 ? "span 2 / span 2" : undefined}
            >
              <HStack justifyContent="space-between" gap={2}>
                <Text
                  as="span"
                  m={0}
                  lineHeight="1.1"
                  fontSize="2xs"
                  color="whiteAlpha.700"
                  fontWeight="semibold"
                >
                  {field.label}
                </Text>
              </HStack>

              {field.type === "switch" ? (
                <Switch.Root
                  checked={Boolean(value)}
                  onCheckedChange={(details) =>
                    formik.setFieldValue(field.name, details.checked)
                  }
                >
                  <Switch.HiddenInput />
                  <Switch.Control />
                </Switch.Root>
              ) : field.type === "select" ? (
                <NativeSelect.Root size="sm">
                  <NativeSelect.Field
                    value={value == null ? "" : String(value)}
                    onChange={(e) =>
                      formik.setFieldValue(field.name, e.target.value)
                    }
                  >
                    {field.options?.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </NativeSelect.Field>
                  <NativeSelect.Indicator>
                    <Pathicon icon="chevron-down" />
                  </NativeSelect.Indicator>
                </NativeSelect.Root>
              ) : field.type === "radio" ? (
                <RadioGroup.Root
                  value={value == null ? "" : String(value)}
                  onValueChange={(details) =>
                    formik.setFieldValue(field.name, details.value)
                  }
                >
                  <HStack gap={2} flexWrap="wrap">
                    {field.options?.map((option) => (
                      <RadioGroup.Item key={option.value} value={option.value}>
                        <RadioGroup.ItemHiddenInput />
                        <RadioGroup.ItemContext>
                          {(item) => (
                            <Box
                              px={2}
                              py={1}
                              borderRadius="md"
                              borderWidth="1px"
                              borderColor={
                                item.checked
                                  ? "whiteAlpha.500"
                                  : "whiteAlpha.300"
                              }
                              bg={
                                item.checked ? "whiteAlpha.300" : "transparent"
                              }
                              fontSize="xs"
                              lineHeight="1.1"
                              cursor="pointer"
                            >
                              {option.label}
                            </Box>
                          )}
                        </RadioGroup.ItemContext>
                      </RadioGroup.Item>
                    ))}
                  </HStack>
                </RadioGroup.Root>
              ) : (
                <Input
                  size="sm"
                  type={field.type === "number" ? "number" : "text"}
                  value={value == null ? "" : String(value)}
                  onChange={(e) =>
                    formik.setFieldValue(field.name, e.target.value)
                  }
                  placeholder={field.placeholder}
                />
              )}

              {field.helpText ? (
                <Text
                  as="span"
                  m={0}
                  lineHeight="1.2"
                  fontSize="2xs"
                  color="whiteAlpha.500"
                >
                  {field.helpText}
                </Text>
              ) : null}
            </VStack>
          )
        })}
      </SimpleGrid>

      <VStack alignItems="stretch" gap={1}>
        <Text
          as="span"
          m={0}
          lineHeight="1.1"
          fontSize="2xs"
          color="whiteAlpha.700"
          fontWeight="semibold"
        >
          Embed code
        </Text>
        <Box position="relative">
          <Textarea
            value={code}
            onChange={(e) => setCode(e.target.value)}
            minH="135px"
            resize="vertical"
            pr={12}
            fontFamily="mono"
            fontSize="xs"
            bg="blackAlpha.500"
            borderColor="whiteAlpha.200"
          />
          <IconButton
            aria-label="Copy embed code"
            size="xs"
            variant="ghost"
            colorPalette="whiteAlpha"
            position="absolute"
            top={2}
            right={2}
            onClick={() =>
              copyToClipboard(code, {
                title: copySuccessTitle,
                description: ""
              })
            }
          >
            <Pathicon icon="copy" />
          </IconButton>
        </Box>
      </VStack>

      <VStack alignItems="stretch" gap={1}>
        <Text
          as="span"
          m={0}
          lineHeight="1.1"
          fontSize="2xs"
          color="whiteAlpha.700"
          fontWeight="semibold"
        >
          Preview
        </Text>
        {previewSrc ? (
          <Box
            borderWidth="1px"
            borderColor="whiteAlpha.200"
            borderRadius="md"
            overflow="hidden"
            bg="blackAlpha.300"
            p={2}
          >
            <iframe
              ref={previewIframeRef}
              key={code}
              src={previewSrc}
              title="Embed preview"
              width="100%"
              height={previewHeight}
              style={{
                border: 0,
                display: "block",
                width: "100%",
                background: "transparent"
              }}
            />
          </Box>
        ) : (
          <Text
            as="span"
            m={0}
            lineHeight="1.2"
            fontSize="2xs"
            color="whiteAlpha.500"
          >
            Preview unavailable. Ensure the embed code contains an iframe src.
          </Text>
        )}
      </VStack>
    </VStack>
  )
}

type EmbedCodeGeneratorProps<TValues extends Record<string, unknown>> = {
  initialValues: TValues
  fields: EmbedFieldConfig<TValues>[]
  buildCode: (values: TValues) => string
  copySuccessTitle?: string
  /** Expected `postMessage` payload `source` for iframe height resize (must match the embedded page). */
  postMessageResizeSource?: string
  /** Expected `postMessage` payload `type` for height resize. */
  postMessageResizeType?: string
}

function EmbedCodeGenerator<TValues extends Record<string, unknown>>({
  initialValues,
  fields,
  buildCode,
  copySuccessTitle = "Copied embed code",
  postMessageResizeSource = EMBED_PREVIEW_RESIZE_POST_MESSAGE_SOURCE_DEFAULT,
  postMessageResizeType = EMBED_PREVIEW_RESIZE_POST_MESSAGE_TYPE_DEFAULT
}: EmbedCodeGeneratorProps<TValues>) {
  return (
    <Formik
      initialValues={initialValues}
      onSubmit={() => {}}
      enableReinitialize
    >
      <Form>
        <EmbedCodeGeneratorForm
          fields={fields}
          buildCode={buildCode}
          copySuccessTitle={copySuccessTitle}
          postMessageResizeSource={postMessageResizeSource}
          postMessageResizeType={postMessageResizeType}
        />
      </Form>
    </Formik>
  )
}

export default EmbedCodeGenerator
